"use strict";

import moment from "moment-timezone";
import OpeningHoursUtil from "./OpeningHoursUtil.js";
import WallClock from "./WallClock.js";

/**
 * Horizonty pujcovny ve dnech -- JEDINY zdroj pravdy pro nejzazsi den vyzvednuti a
 * nejzazsi den vraceni (MOB-049, MOB-525). Pujcovna ma DVA stropy (rozhodnuti product
 * ownera 2026-09-26, nahrazuje "dvanact mesicu" z 2026-08-22):
 *   - vyzvednuti nejpozdeji dnes + 365 dni -> DateUtil.rentPickupMaxDate(),
 *   - vraceni nejpozdeji tentyz kalendarni den za 2 roky -> DateUtil.rentReturnMaxDate(),
 * aby sel rezervovat celosezonni (rocni) pronajem s vyzvednutim az za rok. Vraceni se
 * pocita v kalendarnich letech, ne jako 730 dni: s 29. unorem mezi tim by 730 dni skoncilo
 * den pred vyrocim a rocni pronajem vyzvednuty v posledni den okna by nesel vratit. Hodnota plati
 * pro vsechny tenanty; co je rezervovatelne, tenant ridi oteviraci dobou pobocky.
 * Podlaha = DateUtil.bookingMinDate(rentBookingOffsetHours) (store ji drzi
 * v state.checkInDateMinRent).
 *
 * Horizont ma nezavisle hodnoty pro RENT a SCHOOL (rozhodnuti product ownera
 * 2026-09-17). Nesjednocuji se: zmena jedne nemeni druhou.
 */
const RENT_PICKUP_HORIZON_DAYS = 365;
const RENT_RETURN_HORIZON_YEARS = 2;

/**
 * Horizont skoly ve dnech -- JEDINY zdroj pravdy pro nejzazsi rezervovatelny den vsech
 * tri skolnich toku: privatni lekce (V1 i V2, step-4 picker i step-5 kalendar), skupinove
 * kurzy a kempy (MOB-049). Strop = DateUtil.schoolBookingMaxDate(). Produktove okno
 * (enabledTo, MOB-117) ho muze jen zkratit.
 */
const SCHOOL_BOOKING_HORIZON_DAYS = 280;

class DateUtil {
  /**
   * The writer of a wall-clock time for the API: `WallClock.toWire` (MOB-058). It gives
   * `YYYY-MM-DDTHH:mm:ssZ`, the digits with a constant `Z`. The Postgres column is a timestamp
   * without a zone, so the digits must not move. A string keeps its digits, a moment gives the
   * digits that it shows, and a `Date` gives its local fields. `null` gives `null`, and an
   * unreadable value throws.
   * @param {*} date
   */
  static dateToAWSDateTime(date) {
    return WallClock.toWire(date);
  }

  /**
   * A local `Date` that shows the digits of a wall-clock time of the API, with the seconds and
   * the milliseconds at 0. The digits come from `WallClock.read`, so every old form (`Z`,
   * `+02:00`, `+00:00`, no offset) gives the same `Date` (MOB-058). `null` and an unreadable value
   * give an invalid `Date`.
   */
  static zuluTimeStringToLocalTime(timeString) {
    const m = WallClock.read(timeString);
    if (m == null) {
      return new Date(NaN);
    }
    return new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), 0, 0);
  }

  static fitDateToBussinessHours(
    bhFromHour,
    bhFromMinute,
    bhToHour,
    bhToMinute,
    timeToFit
  ) {
    let dayStart = moment(timeToFit).startOf("day");
    dayStart.hour(bhFromHour);
    dayStart.minute(bhFromMinute);
    dayStart.second(0);

    let dayEnd = moment(timeToFit).startOf("day");
    dayEnd.hour(bhToHour);
    dayEnd.minute(bhToMinute);
    dayEnd.second(0);

    let nextDayStart = moment(timeToFit).startOf("day").add(1, "day");
    nextDayStart.hour(bhFromHour);
    nextDayStart.minute(bhFromMinute);
    nextDayStart.second(0);

    // pred otvirackou -- posun na otviracku daneho dne
    if (timeToFit.isBefore(dayStart)) {
      return dayStart;
    }
    // po otviracce -- posun na otviracku nasledujiciho dne
    else if (timeToFit.isAfter(dayEnd)) {
      return nextDayStart;
    }
    // jinak je v ramci otviracky
    else {
      return timeToFit;
    }
  }

  /**
   * Prvni rezervovatelny den pro PUJCOVNU: den obsahujici okamzik now + offset,
   * posunuty za kazdy okamzik, kdy ma zvolena pobocka zavreno (MOB-227) -- plati
   * vychozi rozvrh i vyjimky a cele zavrene dny se preskakuji. Pobocka bez rozvrhu
   * (legacy tenant) se srovna na pevnou dobu 8:30-18:00 (fitDateToBussinessHours).
   * Pujcovni offset je v HODINACH (rozhodnuto 2026-08-22, MOB-050) -- skolni offsety
   * jsou minuty a maji vlastni funkci bookingMinDateFromMinutes. Hodnotu z konfigurace
   * cti pres BookingOffsetUtil.rentOffsetHours, ktera zna i stary nazev pole.
   * `now` je jen pro predpoved v testech (E2E orakulum pocita podlahu pro okamzik, kdy
   * aplikaci otevrelo); aplikace ho nepredava a plati aktualni cas.
   * Vsechny rentalni ovladace ji ctou pres state.checkInDateMinRent, ktery store
   * plni z teto funkce -- nikde se nepocita znovu.
   */
  static bookingMinDate(offsetHours, defaultSchedule, exceptionSchedules, now) {
    let timeMin = moment(now).add(offsetHours, "hours");
    if (!OpeningHoursUtil._hasAnySchedule(defaultSchedule, exceptionSchedules)) {
      timeMin = DateUtil.fitDateToBussinessHours(8, 30, 18, 0, timeMin);
      return timeMin.format("YYYY-MM-DD");
    }

    // MOB-227: prvni den, kdy ma pobocka jeste po okamziku now + offset otevreno.
    // V den okamziku rozhoduje cas (vyzvednuti jde jen pred zaviraci dobou), dalsi dny
    // staci, ze jsou otevrene. Hledani konci na stropu vyzvednuti -- podlaha je den
    // vyzvednuti, a podlaha nad stropem znamena, ze zadny ovladac nenabidne ani jeden den.
    const timeOfDay = timeMin.format("HH:mm");
    const candidate = moment(timeMin).startOf("day");
    const ceiling = moment(DateUtil.rentPickupMaxDate());
    let first = true;
    while (!candidate.isAfter(ceiling, "day")) {
      const resolved = OpeningHoursUtil.resolveDay(
        defaultSchedule,
        exceptionSchedules,
        candidate
      );
      const open =
        !resolved.closed &&
        (!first || resolved.intervals.some((it) => timeOfDay < it.closes));
      if (open) break;
      candidate.add(1, "day");
      first = false;
    }
    return candidate.format("YYYY-MM-DD");
  }

  /** Pocet dnu horizontu vyzvednuti pujcovny. */
  static get RENT_PICKUP_HORIZON_DAYS() {
    return RENT_PICKUP_HORIZON_DAYS;
  }

  /** Pocet kalendarnich let horizontu vraceni pujcovny. */
  static get RENT_RETURN_HORIZON_YEARS() {
    return RENT_RETURN_HORIZON_YEARS;
  }

  /**
   * Nejzazsi den vyzvednuti (YYYY-MM-DD) -- strop kazdeho ovladace, ktery nastavuje den
   * vyzvednuti: date picker vyzvednuti na strance rezervace, date picker kroku Datum
   * i chuze po dnech v RentTimeslotCalendar.
   */
  static rentPickupMaxDate() {
    return moment().add(RENT_PICKUP_HORIZON_DAYS, "days").format("YYYY-MM-DD");
  }

  /**
   * Nejzazsi den vraceni (YYYY-MM-DD) -- strop date pickeru vraceni: tentyz kalendarni den
   * za 2 roky (z 29. 2. se stane 28. 2.). Je aspon rok za stropem vyzvednuti, takze i
   * pronajem vyzvednuty v posledni den okna jde vratit tentyz den za rok.
   */
  static rentReturnMaxDate() {
    return moment().add(RENT_RETURN_HORIZON_YEARS, "years").format("YYYY-MM-DD");
  }

  /** Pocet dnu rezervacniho horizontu skoly. */
  static get SCHOOL_BOOKING_HORIZON_DAYS() {
    return SCHOOL_BOOKING_HORIZON_DAYS;
  }

  /**
   * Horni mez rezervacniho okna skoly (YYYY-MM-DD) -- nejzazsi den, ktery smi nabidnout
   * kterykoli skolni ovladac: step-4 picker a step-5 kalendar privatnich lekci, date
   * picker skupinovych kurzu i okno vypisu behu kempu.
   */
  static schoolBookingMaxDate() {
    return moment().add(SCHOOL_BOOKING_HORIZON_DAYS, "days").format("YYYY-MM-DD");
  }

  /**
   * Okno, za ktere se nacitaji vyjimky oteviraci doby pobocky: dnes az strop vraceni,
   * aby byl nacteny kazdy den, ktery jde zvolit pro vyzvednuti i pro vraceni (MOB-525).
   * Zacina DNES, ne na podlaze -- podlaha se z vyjimek teprve pocita (MOB-227).
   * Store i stranka rezervace ctou stejne okno, takze sdili jeden dotaz a jednu cache.
   */
  static rentOpenHoursWindow() {
    return {
      windowFrom: moment().format("YYYY-MM-DD"),
      windowTo: DateUtil.rentReturnMaxDate(),
    };
  }

  /**
   * Prvni rezervovatelny den pro SKOLU: den obsahujici okamzik now + offset.
   * Booking offsety jsou MINUTY (rozhodnuto 2026-08-05, __docs/e2e-data-contracts.md C2).
   * Zadny fit na oteviraci dobu — musi souhlasit s checkInMinDate step-4 pickeru
   * (CoursePrivateBookingV2/CourseGroupBooking/CourseCampBooking), ktery cte stejny
   * offset bez fitu (UC-05 B15). Hranicni den se nabizi cely; orez casti dne pred
   * now + offset dela step 5 (CoursePrivateUtil.getEventsByProduct).
   */
  static bookingMinDateFromMinutes(offsetMinutes) {
    return moment().add(offsetMinutes, "minutes").format("YYYY-MM-DD");
  }

  static isWeekend(date) {
    const day = moment(date).day(); // Get day of the week (0 - Sunday, 6 - Saturday)
    return day === 0 || day === 6;  // Return true for weekend
}

  /**
   * Normalize a date-picker value to a local-calendar 'YYYY-MM-DD' string.
   * Vuetify 3's v-date-picker emits Date objects where Vuetify 2 emitted strings,
   * so consumers comparing ISO date strings must go through this instead of
   * String(value).slice(0, 10). Accepts Date | string | null/undefined;
   * empty/nullish input returns "".
   */
  static toISODateString(value) {
    if (value == null || value === "") {
      return "";
    }
    return moment(value).format("YYYY-MM-DD");
  }
}

export default DateUtil;
